import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import type {
  InboxThread,
  InboxThreadEventsListParams,
  InboxThreadEventsListResponse,
  InboxThreadsListParams,
  InboxThreadsListResponse,
  InboxThreadStatus,
} from '../../application/types/inbox.types';

const unwrapApiData = <T>(response: unknown): T => {
  const maybeObject = response as { data?: unknown };
  if (
    maybeObject &&
    typeof maybeObject === 'object' &&
    Object.prototype.hasOwnProperty.call(maybeObject, 'data')
  ) {
    return maybeObject.data as T;
  }
  return response as T;
};

const patchInboxThreadCache = async (
  threadId: number,
  dispatch: (action: unknown) => void,
  queryFulfilled: Promise<{ data: InboxThread }>,
) => {
  try {
    const { data } = await queryFulfilled;
    dispatch(
      inboxApi.util.updateQueryData('getInboxThread', threadId, () => data),
    );
  } catch {
    // keep cached thread when mutation fails
  }
};

const SYSTEM_INBOX_BASE = '/system/inbox';

export const inboxApi = createApi({
  reducerPath: 'inboxApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['InboxThread'],
  endpoints: (build) => ({
    getInboxThreads: build.query<InboxThreadsListResponse, InboxThreadsListParams>({
      query: (params) => ({
        url: `${SYSTEM_INBOX_BASE}/list`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThreadsListResponse>(response),
      providesTags: ['InboxThread'],
    }),

    getInboxThread: build.query<InboxThread, number>({
      query: (threadId) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThread>(response),
      providesTags: (_result, _err, threadId) => [
        { type: 'InboxThread', id: threadId },
      ],
    }),

    getInboxThreadEvents: build.query<
      InboxThreadEventsListResponse,
      InboxThreadEventsListParams
    >({
      query: ({ threadId, limit, offset }) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}/events`,
        method: 'GET',
        params: { limit, offset },
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThreadEventsListResponse>(response),
      providesTags: (_result, _err, { threadId }) => [
        { type: 'InboxThread', id: threadId },
      ],
    }),

    sendInboxMessage: build.mutation<InboxThread, { threadId: number; message: string }>({
      query: ({ threadId, message }) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}/messages`,
        method: 'POST',
        body: { message },
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThread>(response),
      async onQueryStarted({ threadId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            inboxApi.util.updateQueryData('getInboxThread', threadId, () => data),
          );
        } catch {
          // keep cached thread when send fails
        }
      },
      invalidatesTags: ['InboxThread'],
    }),

    markInboxThreadRead: build.mutation<
      { threadId: number; lastReadMessageId: number | null },
      number
    >({
      query: (threadId) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}/read`,
        method: 'PATCH',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<{ threadId: number; lastReadMessageId: number | null }>(
          response,
        ),
      invalidatesTags: ['InboxThread'],
    }),

    changeInboxThreadStatus: build.mutation<
      InboxThread,
      { threadId: number; status: InboxThreadStatus }
    >({
      query: ({ threadId, status }) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}/status`,
        method: 'PATCH',
        body: { status },
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThread>(response),
      async onQueryStarted({ threadId }, { dispatch, queryFulfilled }) {
        await patchInboxThreadCache(threadId, dispatch, queryFulfilled);
      },
      invalidatesTags: (_result, _err, { threadId }) => [
        'InboxThread',
        { type: 'InboxThread', id: threadId },
      ],
    }),

    closeInboxThread: build.mutation<InboxThread, number>({
      query: (threadId) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}/close`,
        method: 'PATCH',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThread>(response),
      async onQueryStarted(threadId, { dispatch, queryFulfilled }) {
        await patchInboxThreadCache(threadId, dispatch, queryFulfilled);
      },
      invalidatesTags: (_result, _err, threadId) => [
        'InboxThread',
        { type: 'InboxThread', id: threadId },
      ],
    }),

    reopenInboxThread: build.mutation<InboxThread, number>({
      query: (threadId) => ({
        url: `${SYSTEM_INBOX_BASE}/${threadId}/reopen`,
        method: 'PATCH',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<InboxThread>(response),
      async onQueryStarted(threadId, { dispatch, queryFulfilled }) {
        await patchInboxThreadCache(threadId, dispatch, queryFulfilled);
      },
      invalidatesTags: (_result, _err, threadId) => [
        'InboxThread',
        { type: 'InboxThread', id: threadId },
      ],
    }),
  }),
});

export const {
  useChangeInboxThreadStatusMutation,
  useCloseInboxThreadMutation,
  useGetInboxThreadEventsQuery,
  useGetInboxThreadQuery,
  useGetInboxThreadsQuery,
  useMarkInboxThreadReadMutation,
  useReopenInboxThreadMutation,
  useSendInboxMessageMutation,
} = inboxApi;
